import React, { useState, useEffect, useContext, useRef, FC, useMemo } from 'react';
import { colors, fontSizes, layout, fontFamily, shadows } from '@/theme';
import Fetch from '@/utils/Fetch';
import * as Enums from '@/utils/enums';
import Helper from '@/utils/helper';
import PageContext from '@/utils/page-context';
import signatureService from '@/services/signature/signature-service';
import AttachmentService from '@/services/attachment-service';
import NoSSR from '@/utils/no-ssr'
import SCInput from '@/components/sc-controls/form-controls/sc-input';
import OldButton from '@/components/button';
import { Button, Flex, Menu, Skeleton } from '@mantine/core';
import SignatureCanvas from 'react-signature-canvas';
import useWindowSize from "@/hooks/useWindowSize";
import useMobileView from '@/hooks/useMobileView';
import ToastContext from '@/utils/toast-context';
import LeadMessage from '@/components/customer-zone/general/lead-message';
import { Attachment, Signature } from '@/interfaces/api/models';
import storage from '@/utils/storage';
import constants from '@/utils/constants';
import useRefState from '@/hooks/useRefState';
import SignatureTerms from '@/PageComponents/Signature/SignatureTerms';
import helper from '@/utils/helper';
import time from '@/utils/time';
import { useRouter } from 'next/router';
import Router from 'next/router';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router'

const SignaturePage: FC = () => {
    const router = useRouter();
    const { t: tenantID, i: signatureID } = router.query as { t: string; i: string }; // TODO(tanstack-router): verify router.query → useParams/useSearch

    const { data: tenantCheckData } = useQuery(
        ['tenant-prelоgin', tenantID],
        () => Fetch.preLogin({ tenantID } as any),
        { enabled: !!tenantID }
    );

    const tenant = tenantCheckData?.HttpStatusCode === 200 && tenantCheckData?.Results?.length > 0
        ? tenantCheckData.Results[0]
        : null;

    const headerTenantName = tenant?.Name ?? '';
    const redirect = (!tenantCheckData || tenant) ? null : `/tenantzone?t=${tenantID}`;

    const pageContext = useContext(PageContext);
    const [signature, setSignature] = useState<Signature | null>(null);
    const [alreadySignedOnUrl, setAlreadySignedOnUrl] = useState(false);

    useEffect(() => {
        if (!redirect && tenant) {
            (pageContext as any).updateTenantName(headerTenantName);
        }
    }, [tenant, redirect]);

    useEffect(() => {
        if (redirect) {
            Router.push(redirect);
        }
    }, [redirect]);

    const windowSize = useWindowSize();
    const [mobileView] = useMobileView();

    const [contactName, setContactName] = useState('');

    const handleContactChange = (e: any) => {
        setContactName(e.value);
        setSignature({
            ...signature,
            SignedBy: e.value
        });
    };

    const getSignedSignatures = () => {
        let signs = localStorage.getItem(Enums.LocalStorage.TenantZoneSignatures);
        try {
            let signArr = JSON.parse(signs ?? "[]");
            if (!Array.isArray(signArr)) {
                return [];
            }
            return signArr;
        } catch (error) {
            return [];
        }
    };

    const setSignedSignatures = (signatureURL: string) => {
        var presignedSignatures = getSignedSignatures();

        presignedSignatures.push(signatureURL);

        localStorage.setItem(Enums.LocalStorage.TenantZoneSignatures, JSON.stringify(presignedSignatures));
    };

    const getUrl = () => {
        return window.location.href;
    }

    useEffect(() => {
        if (!tenant || !signatureID) return;
        var presignedSignatures = getSignedSignatures();
        var url = getUrl();
        if (presignedSignatures.includes(url)) {
            setAlreadySignedOnUrl(true);
        }
        getSignature();
    }, [tenant, signatureID]);

    const setSignatureLocal = (signatureToUpdate: Signature | null) => {
        setSignature(signatureToUpdate);
        setContactName(signatureToUpdate?.SignedBy ?? "");
    }

    const getSignature = async () => {
        let sig = await signatureService.getSignatureTenantZone(signatureID, tenantID, tenant.API);
        setSignatureLocal(sig);
    };

    const [showSignatureOverlay, setShowSignatureOverlay] = useState(false);
    const [showThankYou, setShowThankYou] = useState(false);

    const signatureClick = () => {
        setShowSignatureOverlay(true);
    };

    const signatureRef = useRef<any>(null);
    const [signatureData, setSignatureData] = useState<any>();

    const clearSignature = () => {
        signatureRef.current.clear();
        setSignatureData(null);
    };

    const signatureCallback = (e: any) => {
        let temp = signatureRef.current.toDataURL().replace("data:image/png;base64,", "");
        setSignatureData(temp);
    };

    const [signatureCanvasWidth, setSignatureCanvasWidth] = useState(0);
    const [signatureCanvasHeight, setSignatureCanvasHeight] = useState(0);

    const isPortrait = () => {
        if (!windowSize) return false;
        const windowSizeHeight = windowSize.height as number;
        const windowSizeWidth = windowSize.width as number;
        return windowSizeHeight > windowSizeWidth;
    }

    useEffect(() => {
        if (windowSize && showSignatureOverlay) {
            let windowSizeHeight = windowSize.height as number;
            let windowSizeWidth = windowSize.width as number;

            if (isPortrait()) {
                // portrait
                let width = windowSizeWidth - 32;
                let height = windowSizeHeight - 145;
                setSignatureCanvasWidth(width);
                setSignatureCanvasHeight(height);
            } else {
                // landscape
                let width = windowSizeWidth - 160;
                let height = windowSizeHeight - 72;
                setSignatureCanvasWidth(width);
                setSignatureCanvasHeight(height);
            }
        }
    }, [showSignatureOverlay, windowSize]);

    const [signatureCardMaxHeight, setSignatureCardMaxHeight] = useState('unset');
    const [thumbnailMaxHeight, setThumbnailMaxHeight] = useState('unset');

    useEffect(() => {
        if (windowSize) {
            const windowSizeHeight = windowSize.height as number;
            let newHeight = windowSizeHeight - 130;
            setSignatureCardMaxHeight(`${newHeight}px`);
            setThumbnailMaxHeight(`${newHeight / 2}px`);
        }
    }, [windowSize]);

    const toast = useContext(ToastContext);
    const [inputErrors, setInputErrors] = useState<any>({});

    const signatureAttachment = useMemo(() => {
        return signature?.Attachment;
    }, [signature]);

    const hasSignatureAttachment = useMemo(() => {
        return !!signature?.Attachment;
    }, [signature]);

    const validate = () => {

        let validationItems: any[] = [];

        // Customer name but no signature
        if (!Helper.isNullOrWhitespace(contactName) && !signatureAttachment) {
            validationItems = [...validationItems,
            { key: "Signature", value: signatureData, required: true, type: Enums.ControlType.Custom }
            ];
        }

        // No Customer name with signature
        if (Helper.isNullOrWhitespace(contactName) && signatureAttachment) {
            validationItems = [...validationItems,
            { key: "Contact", value: contactName, required: true, type: Enums.ControlType.Text },
            ];
        }

        const { isValid, errors } = Helper.validateInputs(validationItems);

        setInputErrors(errors);
        return isValid;
    };

    const signatureTerms = useMemo(() => {
        return signature?.Terms ?? signature?.SignatureTemplate?.Terms;
    }, [signature]);

    const saveSignature = async () => {
        const id = helper.newGuid();

        let attachment: Attachment = {
            ID: id,
            AttachmentType: Enums.AttachmentType.Signature,
            ContentType: 'image/png',
            Description: `Signature ${signature?.Key}`,
            ItemID: signature?.ID,
            FileBase64: signatureData,
            FileName: `${id}.png`,
            UserType: Enums.UserType.Employee,
            FileSize: signatureData.length,
            Module: Enums.Module.Signature
        };

        let signatureToSave: Signature = { ...signature };

        signatureToSave.Attachment = undefined;
        signatureToSave.AttachmentID = attachment.ID;
        signatureToSave.SignedDate = time.toISOString(time.now());
        signatureToSave.SignatureSource = Enums.SignatureSource.WebApp;

        return await signatureService.saveSignatureTenantZone({
            Signature: signatureToSave,
            Attachment: attachment
        }, tenantID, tenant.API, null, toast);
    };

    const [updating, setUpdating] = useState(false);
    const [closingCount, setClosingCount, getClosingCountValue] = useRefState(3);
    const closeCountdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

    useEffect(() => () => {
        if (closeCountdownRef.current) clearInterval(closeCountdownRef.current);
    }, []);

    const startCloseCountdown = () => {
        if (closeCountdownRef.current) return;
        closeCountdownRef.current = setInterval(() => {
            const count = getClosingCountValue() - 1;
            setClosingCount((_: any) => count);
            if (count <= 0) {
                clearInterval(closeCountdownRef.current!);
                closeCountdownRef.current = null;
                window.close();
            }
        }, 1_000);
    };

    const updateSignature = async () => {
        setUpdating(true);
        if (signatureData) {
            let savedSignature = await saveSignature();
            setShowSignatureOverlay(false);
            setUpdating(false);
            setSignatureLocal(savedSignature);
            setShowSignatureOverlay(false);
            setShowThankYou(true);
            setSignedSignatures(getUrl());
            startCloseCountdown();
        }
        else {
            (toast as any).setToast({
                message: 'Please provide a signature',
                show: true,
                type: Enums.ToastType.error
            });
            setUpdating(false);
        }
    };

    const signatureOverlay = () => {
        return (
            <div className='signature-overlay'>

                <NoSSR>
                    <div className='signature'>
                        <SignatureCanvas ref={signatureRef} onEnd={signatureCallback}
                            canvasProps={{ width: signatureCanvasWidth, height: signatureCanvasHeight, className: 'signatureCanvas' }} />

                        <div className="signature-title">{signature?.Title}</div>
                    </div>
                </NoSSR>

                <div className="button-container">
                    <div className='clear'>
                        <Button
                            onClick={clearSignature}
                            disabled={!signatureData}
                            size={'xs'}
                            variant={'outline'}
                        >
                            Clear
                        </Button>
                    </div>

                    <div className='update'>
                        <Button
                            onClick={updateSignature}
                            disabled={!signatureData || !(signature?.SignedBy) || updating}
                            size={'xs'}
                        >
                            {updating ? "Uploading..." : signatureTerms ? "Confirm" : "Confirm"}
                        </Button>
                    </div>

                    <div className='disclaimer'>
                        <SignatureTerms
                            label='View Terms'
                            terms={signatureTerms}

                            maxWidth={isPortrait() ? "calc(100vw - 3rem)" : ""}
                        />
                    </div>

                    <div className='cancel'>
                        <Button
                            onClick={() => {
                                setShowSignatureOverlay(false);
                                setSignatureData(null);
                            }}
                            size={'xs'}
                            variant={'outline'}
                        >
                            Cancel
                        </Button>
                    </div>
                </div>

                <style jsx>{`
                    .signature-overlay {
                        position: fixed;
                        top: 0;
                        left: 0;
                        display: flex;
                        flex-direction: column;
                        background-color: ${colors.backgroundGrey};
                        width: 100%;
                        height: 100%;
                        z-index: 1;
                        padding: 1rem;
                        box-sizing: border-box;
                        overflow-y: auto;
                    }
                    .signature-header {
                        display: flex;
                        justify-content: center;
                        font-weight: bold;
                    }
                    
                    .signature {
                        display: flex;
                        margin-top: 0rem;
                        position: relative;
                    }

                    .signature-title {
                        position: absolute;
                        pointer-events: none;
                        left: 1rem;
                        top: 1rem;
                        opacity: 0.5;
                        max-width: ${signatureCanvasWidth - 24}px;
                    }

                    .button-container {
                    }

                    .disclaimer {
                        background: #f4f4f4;
                        padding: 0.1rem;
                        border-radius: 4px;
                    }

                    @media all and (orientation:portrait) {

                        .button-container {
                            position: relative;
                            top: 0.5rem;
                            left: 0;
                            width: 100%;
                            height: 16rem;
                        }

                        .clear {
                            position: absolute;
                            bottom: 2.2rem;
                            right: 0rem;
                            text-align: right;
                        }
                        .update {
                            position: absolute;
                            bottom: 0rem;
                            right: 0rem;
                        }
                        .disclaimer {
                            position: relative;
                            top: 0.5rem;
                            overflow: auto;
                            max-height: 10rem;
                        }
                        .cancel {
                            position: absolute;
                            bottom: 0rem;
                            left: 0rem;
                        }
                    }

                    @media all and (orientation:landscape) {
                        .clear {
                            position: absolute;
                            top: 1rem;
                            right: 1rem;
                        }
                        .update {
                            position: absolute;
                            bottom: 3.5rem;
                            right: 1rem;
                        }
                        .disclaimer {
                            position: absolute;
                            bottom: 1rem;
                            left: 1rem;
                            width: calc(100vw - 10rem);
                            overflow: auto;
                            max-height: calc(100vh - 10.5rem);
                        }
                        .cancel {
                            position: absolute;
                            bottom: 1rem;
                            right: 1rem;
                        }
                    }
                `}</style>
            </div>
        )
    };

    const thankYou = () => {
        return (
            <div className='signature-overlay'>
                <div className='signature-header'>
                    {signature?.Title}
                </div>
                <div className='thankyou-message'>
                    Thank you for signing!
                </div>
                <div className='sub-message'>
                    This page will close automatically in {closingCount}s...
                </div>
                <style jsx>{`
                    .signature-overlay {
                        position: fixed;
                        top: 0;
                        left: 0;
                        display: flex;
                        flex-direction: column;
                        background-color: ${colors.backgroundGrey};
                        width: 100%;
                        height: 100%;
                        z-index: 1;
                        padding: 1rem;
                        box-sizing: border-box;
                    }
                    .signature-header {
                        display: flex;
                        justify-content: center;
                        font-weight: bold;
                    }
                    .thankyou-message {
                        display: flex;
                        justify-content: center;
                        margin-top: 3rem;
                    }
                    .sub-message {
                        display: flex;
                        justify-content: center;
                        margin-top: 1rem;
                        font-size: 0.8rem;
                    }
                `}</style>
            </div>
        )
    };

    const alreadySigned = () => {
        return (
            <div className='signature-overlay'>
                <div className='signature-header'>
                    {signature?.Title}
                </div>
                <div className='thankyou-message'>
                    Thank you for signing!
                </div>
                <div className='sub-message'>
                    If you need to re-sign, please request a new signature.
                </div>
                <style jsx>{`
                    .signature-overlay {
                        position: fixed;
                        top: 0;
                        left: 0;
                        display: flex;
                        flex-direction: column;
                        background-color: ${colors.backgroundGrey};
                        width: 100%;
                        height: 100%;
                        z-index: 1;
                        padding: 1rem;
                        box-sizing: border-box;
                    }
                    .signature-header {
                        display: flex;
                        justify-content: center;
                        font-weight: bold;
                    }
                    .thankyou-message {
                        display: flex;
                        justify-content: center;
                        margin-top: 3rem;
                    }
                    .sub-message {
                        display: flex;
                        justify-content: center;
                        margin-top: 1rem;
                        font-size: 0.8rem;
                    }
                `}</style>
            </div>
        )
    };

    return (
        <NoSSR>
            <div className='signature-container'>

                <LeadMessage customerID={""} tenantID={tenantID} page={"tenantzone/signature"} />

                <div className="signature-inner-container">
                    <div className='signature-header'>
                        {signature?.Title}
                    </div>

                    <div className='signature-card'>
                        <div className='contact-name'>
                            <SCInput name="Contact" label="Your Name" value={contactName} required={true} onChange={handleContactChange} error={inputErrors.Contact} />
                        </div>
                        <SignatureTerms
                            label='View Terms'
                            terms={signatureTerms}
                            maxWidth={"calc(100vw - 3rem)"}
                        />
                        <div className='sign-button'>
                            <Button
                                onClick={signatureClick}
                                size={'xs'}
                                disabled={!contactName}
                                leftSection={<span><img src="/specno-icons/edit.svg" height={16} style={{ filter: "brightness(10000%)" }} /></span>}
                            >
                                Sign
                            </Button>
                        </div>
                        {hasSignatureAttachment ?
                            <div className='current-signature'>
                                <img src={signature?.Attachment?.Url} className="thumb" />
                            </div> : ''
                        }
                    </div>

                    {/* <div className='submit-button'>
                    <Button text="Submit" onClick={submit} disabled={submitting} extraClasses="fit-content no-margin" />
                </div> */}
                </div>

                {showSignatureOverlay ? signatureOverlay() : ''}

                {showThankYou ? thankYou() : ''}

                {alreadySignedOnUrl ? alreadySigned() : <></>}

            </div>
            <style jsx>{`

                .signature-container {
                    position: fixed;
                    top: 0;
                    left: 0;
                    display: flex;
                    flex-direction: column;
                    background-color: ${colors.backgroundGrey};
                    width: 100%;
                    height: 100%;
                    z-index: 1;
                    padding: 1rem;
                    box-sizing: border-box;
                    overflow: auto;
                }

                

                .signature-header {
                    display: flex;
                    font-weight: bold;
                    margin-bottom: 0.5rem;
                }
                .signature-card {
                    display: flex;
                    flex-direction: column;
                    padding: 0.5rem;
                    background: white;
                    box-shadow: ${shadows.card};
                    margin: 0 0 0.5rem 0;
                    border-radius: 3px;
                    position: relative;
                    /* max-height: ${signatureCardMaxHeight}; */
                }
                .thumb {
                    max-height: ${thumbnailMaxHeight};
                }
                .contact-name {
                }
                .current-signature {
                    display: flex;
                    justify-content: center;
                }
                .current-signature img {
                    max-width: 100%;
                    max-height: 100%;
                }
                .sign-button {
                    margin-top: 0.5rem;
                }
                .submit-button {
                    display: flex;
                    justify-content: flex-end;
                }

            `}</style>
        </NoSSR>
    )
};

export default SignaturePage;

export const Route = createFileRoute('/tenantzone/signature')({ component: SignaturePage })
